import { ProductAttributeValue } from "@/lib/product-variants";"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/hooks/use-cart";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

import {
  ProductVariant,
  AttributeWithValues,
  fetchProductVariants,
  fetchProductAttributes,
  formatVariantAttributes,
  getGroupedAttributeValues,
  findVariantByAttributes
} from "@/lib/product-variants";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { 
  Minus,
  Plus,
  ShoppingCart,
  X
} from "lucide-react";
import { toast } from "sonner";

interface Product {
  id: number;
  name: string;
  price: number;
  slug: string;
  stock: number;
  description: string;
  image_url: string;
  has_variants: boolean;
}

interface ProductQuickViewProps {
  product: Product;
  open: boolean;
  onClose: () => void;
}

export default function ProductQuickView({ product, open, onClose }: ProductQuickViewProps) {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  
  // Variant state
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [attributes, setAttributes] = useState<AttributeWithValues[]>([]);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<Record<string, string>>({});
  const [loadingVariants, setLoadingVariants] = useState(false);

  // Load variants when dialog opens
  useEffect(() => {
    const loadVariantsData = async () => {
      if (!product.has_variants) return;
      
      setLoadingVariants(true);
      try {
        // Load product variants
        const productVariants = await fetchProductVariants(product.id);
        setVariants(productVariants);
        
        // Set default variant
        const defaultVariant = productVariants.find(v => v.is_default) || productVariants[0];
        if (defaultVariant) {
          setSelectedVariant(defaultVariant);
          
          // Initialize selected attribute values
          const initialValues: Record<string, string> = {};
          defaultVariant.attributes?.forEach(attr => {
            if (attr.attribute_value?.attribute) {
              initialValues[attr.attribute_value.attribute.id] = attr.attribute_value_id;
            }
          });
          setSelectedAttributeValues(initialValues);
        }
        
        // Load attribute definitions
        const productAttributes = await fetchProductAttributes();
        setAttributes(productAttributes);
      } catch (error) {
        console.error('Error loading variants:', error);
        toast.error('Failed to load product variants');
      } finally {
        setLoadingVariants(false);
      }
    };
    
    if (open) {
      // Reset quantity when dialog opens
      setQuantity(1);
      
      // Load variants if product has them
      if (product.has_variants) {
        loadVariantsData();
      }
    }
  }, [open, product]);
  

  
  // Handle attribute selection change
  const handleAttributeChange = (attributeId: string, valueId: string) => {
    const newValues = { ...selectedAttributeValues, [attributeId]: valueId };
    setSelectedAttributeValues(newValues);
    
    // Find matching variant based on selected attributes
    const matchingVariant = findVariantByAttributes(variants, newValues);
    if (matchingVariant) {
      setSelectedVariant(matchingVariant);
    }
  };

  // Get current price based on variant selection
  const getCurrentPrice = (): number => {
    if (product.has_variants && selectedVariant) {
      return selectedVariant.price;
    }
    return product.price;
  };
  
  // Get current stock based on variant selection
  const getCurrentStock = (): number => {
    if (product.has_variants && selectedVariant) {
      return selectedVariant.stock;
    }
    return product.stock;
  };
  
  const formattedPrice = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(getCurrentPrice());
  
  const increaseQuantity = () => {
    const currentStock = getCurrentStock();
    if (quantity < currentStock) {
      setQuantity(quantity + 1);
    } else {
      toast.error(`Sorry, only ${currentStock} items in stock`);
    }
  };
  
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  
  const handleAddToCart = () => {
    // Check if product has variants and one is selected
    if (product.has_variants && !selectedVariant) {
      toast.error('Please select options for this product');
      return;
    }
    
    // Check stock availability
    const currentStock = getCurrentStock();
    if (quantity > currentStock) {
      toast.error(`Sorry, only ${currentStock} available in stock`);
      return;
    }
    
    // Add product or variant to cart
    if (product.has_variants && selectedVariant) {
      const variantInfo = {
        ...product,
        id: product.id, // Keep product ID the same
        price: selectedVariant.price,
        stock: selectedVariant.stock,
        variant_id: selectedVariant.id,
        variant_name: formatVariantAttributes(selectedVariant.attributes || []),
      };
      
      for (let i = 0; i < quantity; i++) {
        addItem(variantInfo);
      }
      
      
    } else {
      for (let i = 0; i < quantity; i++) {
        addItem(product);
      }
    }
    
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] h-auto max-h-[90vh] overflow-auto">
        <DialogHeader>
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative h-80 md:h-full bg-muted rounded-md overflow-hidden">
            <Image
              src={product.image_url}
              alt={product.name}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
              className="object-cover"
            />
          </div>

          <div>
            <div className="flex flex-col space-y-4">
              <h3 className="text-xl font-bold">{product.name}</h3>
              <p className="text-gray-600 line-clamp-3">{product.description}</p>
              <p className="text-2xl font-bold">{formattedPrice}</p>
              
              {/* Variant Selection */}
              {product.has_variants && (
                <div className="mt-2 border rounded-md p-3 bg-gray-50">
                  <h4 className="font-medium mb-2">Product Options</h4>
                  
                  {loadingVariants ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                      <span className="ml-2 text-sm text-gray-500">Loading options...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {attributes.map((attribute) => {
                        const groupValues = getGroupedAttributeValues(attribute.id, variants);
                        if (groupValues.length === 0) return null;
                        
                        return (
                          <div key={attribute.id} className="mb-2">
                            <p className="text-sm font-medium mb-1">{attribute.name}</p>
                            <div className="flex flex-wrap gap-2">
                              {groupValues.map((value: ProductAttributeValue) => {
                                const isSelected = selectedAttributeValues[attribute.id] === value.id;
                                
                                return (
                                  <Badge 
                                    key={value.id}
                                    variant={isSelected ? "default" : "outline"}
                                    className={`cursor-pointer ${isSelected ? 'bg-[#9A2643]' : ''}`}
                                    onClick={() => handleAttributeChange(attribute.id, value.id)}
                                  >
                                    {value.value}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Selected variant summary */}
                      {selectedVariant && (
                        <div className="text-sm mt-2 pt-2 border-t border-gray-200">
                          <p className="font-medium">
                            Selected: {formatVariantAttributes(selectedVariant.attributes || [])}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            {selectedVariant.stock > 0 
                              ? `${selectedVariant.stock} in stock` 
                              : 'Out of stock'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center mt-2">
                <label className="text-sm font-medium mr-3">Quantity:</label>
                <div className="flex items-center border rounded-md">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                    className="h-8 w-8"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center">{quantity}</span>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={increaseQuantity}
                    disabled={quantity >= getCurrentStock()}
                    className="h-8 w-8"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button 
                className="w-full" 
                onClick={handleAddToCart}
                disabled={product.has_variants ? (selectedVariant ? selectedVariant.stock === 0 : true) : product.stock === 0}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                {product.has_variants ? 'Add Selected Option to Cart' : 'Add to Cart'}
              </Button>
              
              <Button variant="outline" className="w-full" asChild>
                <Link href={`/product/${product.slug}`}>
                  View Full Details
                </Link>
              </Button>
            </div>
            
            <div className="mt-4 text-sm">
              <p className="text-muted-foreground">
                Availability: 
                <span className={product.stock > 0 ? "text-green-500" : "text-red-500"}>
                  {product.stock > 0 ? " In Stock" : " Out of Stock"}
                </span>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}